import { Link } from 'gatsby';
import React, { FC } from 'react';
import { css } from 'styled-components';
import { I18n } from '../i18n';
import { Language } from '../interfaces';
import { linkReset, zIndex } from './styles';
import { GenderId } from '../gender';
import { desktopMedia, padding } from '../templates/shopPage/constants';

export type NewNavigationBarItems = Array<{
    title: string;
    slug: string;
    disabled?: boolean;
}>;

export const NewNavigationBar: FC<{
    className?: string;
    items: NewNavigationBarItems;
    language: Language;
    gender: GenderId;
}> = ({ className, items, language, gender }) => {
    return (
        <div
            className={className}
            css={`
                box-sizing: border-box;
                margin: 0 -${padding}; //ignore shopPage padding
                *,
                *:before,
                *:after {
                    box-sizing: inherit;
                }
                pointer-events: none;
            `}
        >
            <div
                css={`
                    z-index: ${zIndex.pageNavigation};
                    display: flex;
                `}
            >
                <div
                    css={`
                        overflow-x: auto;
                        scroll-snap-type: x;
                        scroll-snap-align: start;
                        max-width: 100%;
                        display: flex;
                        margin-right: auto;
                        margin-left: auto;
                        align-items: center;
                        pointer-events: auto;
                        white-space: nowrap;
                    `}
                >
                    {items.map(item => {
                        if (item.disabled) return;
                        const typeSlug = item.slug;
                        return (
                            <NewNavigationBarItem
                                key={item.title}
                                to={`/${language}/shop/${gender}/${typeSlug}`}
                                disabled={item.disabled ?? false}
                            >
                                <I18n>{item.title}</I18n>
                            </NewNavigationBarItem>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const NewNavigationBarItem: FC<{
    to: string;
    disabled: boolean;
    children: React.ReactNode;
}> = ({ children, to, disabled }) => {
    // This example assumes that any internal link (intended for Gatsby)
    // will start with exactly one slash, and that anything else is external.
    const internal = /^\/(?!\/)/.test(to);

    if (!internal) {
        return (
            <a
                href={to}
                css={`
                    ${linkReset}
                    font-style: normal;
                    font-weight: 500;
                    font-size: 19px;
                    line-height: 25px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding-bottom: 8px;
                    padding-left: ${padding}; //add shopPage padding in scroll
                    padding-right: 25px;

                    ${disabled &&
                    css`
                        color: #d6d6d6;
                        cursor: default;
                    `}

                    text-underline-offset: 8px;
                    ${!disabled &&
                    css`
                        @media ${desktopMedia} {
                            &:hover {
                                text-decoration: underline;
                            }
                        }
                    `}
                `}
            >
                {children}
            </a>
        );
    }
    return (
        <Link
            to={to}
            activeClassName={'active'}
            activeStyle={{ textDecoration: `underline` }}
            partiallyActive={false}
            aria-current={disabled ? 'location' : 'page'}
            css={`
                ${linkReset}

                font-style: normal;
                font-weight: 500;
                font-size: 19px;
                line-height: 25px;
                display: flex;
                flex-direction: column;
                align-items: center;
                padding-bottom: 30px;
                padding-top: 30px;
                padding-left: ${padding}; //add shopPage padding in scroll
                padding-right: 25px;

                ${disabled &&
                css`
                    color: #d6d6d6;
                    cursor: default;
                `}

                text-underline-offset: 8px;
                ${!disabled &&
                css`
                    @media ${desktopMedia} {
                        &:hover {
                            text-decoration: underline;
                        }
                    }
                    @media (hover: none) {
                        text-decoration: none;
                    }
                `}
            `}
        >
            {children}
        </Link>
    );
};
